// src/components/pipelines/PipelineInfoDrawer.js
import React, { useMemo, useCallback } from 'react';
import {
    Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent,
    DrawerCloseButton, Box, VStack, HStack, Text, Divider, Tag, Icon,
    Heading, Badge, useColorModeValue, Flex,
    // --- UPDATE: additions for the clearance section
    Button, SimpleGrid, Tooltip, Alert, AlertIcon,
    // --- END UPDATE
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react'; // FIX: Imported from @emotion/react
import {
    MdBusiness, MdStraighten, MdCalendarToday, MdLocationOn, MdSensors,
    // --- UPDATE: clearance icons
    MdEdit, MdShield, MdWarning, MdCheckCircle, MdErrorOutline,
    // --- END UPDATE
} from 'react-icons/md';
import { FaRoute } from "react-icons/fa";
// --- UPDATE: icons for cover depth and provenance
import { FaArrowDown, FaClipboardCheck } from "react-icons/fa";
// --- END UPDATE

const pulseRing = keyframes`
  0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(72, 187, 120, 0); }
  100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

// --- UPDATE: clearance constants and helpers --------------------------------

// Mirrors PIPELINE_STATUSES in services/pipelineService.js. Kept as a local
// literal so this presentational component keeps no service dependency.
const EXCLUDED_FROM_SCANS = ['decommissioned', 'abandoned', 'removed'];

const DEPTH_SOURCE_LABELS = {
    field_survey: 'Field survey / locate',
    as_built: 'As-built drawing',
    estimated: 'Estimated from standard',
};

// A reading older than this is stale — matching the 5-minute TTL that
// ReadingTable and WaterLevelTable already use.
const DEFAULT_READING_TTL_MS = 300000;

/**
 * Formats a date without the UTC shift.
 *
 * `new Date('2020-05-01').toLocaleDateString()` parses the string as UTC
 * midnight and renders it in local time — at UTC-4 that displays 30 April.
 * A plain YYYY-MM-DD string is built from its parts instead.
 */
const formatDateSafe = (value) => {
    if (!value) return 'N/A';
    const ymd = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
        const [, y, m, d] = ymd;
        return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString();
};
// --- END UPDATE

// Helper component for displaying a piece of information
const InfoRow = ({ icon, label, value, accent }) => {
    // Hooks must be at the top level of the function component
    const textColor = useColorModeValue('gray.800', 'white');
    const rowBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const rowBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
    const iconBg = useColorModeValue('white', 'gray.700');

    return (
        <HStack w="100%" align="center" spacing={4} p={3} bg={rowBg} borderRadius="lg" borderWidth="1px" borderColor={rowBorder}>
            <Flex p={2} bg={iconBg} borderRadius="md" shadow="sm">
                {/* --- UPDATE: optional accent colour so a clearance row can
                    signal state without needing a second component. Defaults
                    to the original purple. */}
                <Icon as={icon} color={accent || 'purple.500'} boxSize={4} />
                {/* --- END UPDATE */}
            </Flex>
            <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase">{label}</Text>
                <Text fontWeight="800" color={textColor}>{value || 'N/A'}</Text>
            </Box>
        </HStack>
    );
};

const PipelineInfoDrawer = ({
    pipeline,
    sensors = [],
    readings = {},
    isOpen,
    onClose,
    // --- UPDATE: optional wiring for the clearance workflow ----------------
    // sensorPipelineLinks is how the rest of the app resolves sensor↔pipeline
    // relationships; onEdit opens PipelineEditDrawer. Both optional and
    // defaulted, so existing call sites keep working untouched.
    sensorPipelineLinks = {},
    onEdit,
    readingTtlMs = DEFAULT_READING_TTL_MS,
    // --- END UPDATE
}) => {
    // FIX: All hooks moved to the absolute top of the component
    const drawerBg = useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    
    // Hooks for the map items (hoisted to avoid calling hooks inside callbacks)
    const cardBg = useColorModeValue('white', 'gray.800');

    // --- UPDATE: hoisted colours for the clearance section -----------------
    const okColor = useColorModeValue('green.600', 'green.300');
    const warnColor = useColorModeValue('orange.600', 'orange.300');
    const badColor = useColorModeValue('red.600', 'red.300');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const clearanceBg = useColorModeValue('orange.50', 'whiteAlpha.50');
    // --- END UPDATE

    // Calculate associated sensors
    const associatedSensors = useMemo(() => {
        if (!pipeline || !sensors.length) return [];

        // --- UPDATE: broadened sensor matching -----------------------------
        // The original condition was `sensor.pipeline_id === pipeline.id`
        // alone. Nothing else in the app writes a `pipeline_id` field onto the
        // sensor object — links are resolved through the sensorPipelineLinks
        // map, or through the polymorphic linked_entity_type /
        // linked_entity_id pair. The result was that this drawer reported
        // "No sensors mapped to this pipeline" for nearly every pipeline,
        // however many sensors were actually attached.
        //
        // The original check is kept as the first branch; the other two are
        // added alongside it. Comparison is string-normalised because the ids
        // arrive as numbers from one source and strings from another.
        const pid = String(pipeline.id);

        return sensors.filter(sensor => {
            const deviceId = sensor.device_id || sensor.id || sensor.deviceId;

            // 1. Original direct column
            if (sensor.pipeline_id === pipeline.id) return true;
            if (sensor.pipeline_id != null && String(sensor.pipeline_id) === pid) return true;

            // 2. Polymorphic link
            if (sensor.linked_entity_type === 'pipeline'
                && sensor.linked_entity_id != null
                && String(sensor.linked_entity_id) === pid) return true;

            // 3. The link map the page already maintains
            if (deviceId != null
                && sensorPipelineLinks[deviceId] != null
                && String(sensorPipelineLinks[deviceId]) === pid) return true;

            return false;
        });
        // --- END UPDATE
    }, [pipeline, sensors, sensorPipelineLinks]);

    // --- UPDATE: clearance state derived from the new pipeline fields ------
    const clearance = useMemo(() => {
        if (!pipeline) return null;

        const sridOk = pipeline.geometry_srid_ok !== false;
        const isExcluded = EXCLUDED_FROM_SCANS.includes(pipeline.status);
        const hasDepth = pipeline.depth_m !== null
            && pipeline.depth_m !== undefined
            && pipeline.depth_m !== '';

        if (!sridOk) {
            return {
                tone: 'error', color: badColor, icon: MdErrorOutline,
                label: 'Not usable by clearance scans',
                detail: 'Geometry is not SRID 4326, so the subsurface scan raises a database error against this main and returns INDETERMINATE. Run the SRID repair migration.',
            };
        }
        if (isExcluded) {
            return {
                tone: 'warning', color: warnColor, icon: MdWarning,
                label: `Excluded from clearance scans (${pipeline.status})`,
                detail: 'The record is retained, but excavation checks will not flag this main.',
            };
        }
        if (!hasDepth) {
            return {
                tone: 'info', color: warnColor, icon: MdWarning,
                label: 'Flagged at every excavation depth',
                detail: 'No cover depth is recorded. The scan treats unknown depth as in-scope — safe, but it means this main appears in every clearance result regardless of how shallow the dig is.',
            };
        }
        return {
            tone: 'success', color: okColor, icon: MdCheckCircle,
            label: 'Scan-ready',
            detail: `Flagged only when an excavation reaches ${pipeline.depth_m} m, plus the vertical safety buffer.`,
        };
    }, [pipeline, okColor, warnColor, badColor]);

    const handleEditClick = useCallback(() => {
        if (onEdit && pipeline) onEdit(pipeline);
    }, [onEdit, pipeline]);
    // --- END UPDATE

    // Conditional return must happen AFTER all hooks are called
    if (!pipeline) return null;

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
            <DrawerOverlay backdropFilter="blur(4px)" />
            <DrawerContent bg={drawerBg} backdropFilter="blur(16px)" borderLeftWidth="1px" borderColor={borderColor}>
                <DrawerCloseButton mt={2} />
                <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
                    <HStack spacing={3}>
                        <Flex p={2} bg="purple.500" borderRadius="lg" shadow="md">
                            <Icon as={FaRoute} color="white" boxSize={5} />
                        </Flex>
                        <Heading size="md" fontWeight="800">Pipeline Intelligence</Heading>
                    </HStack>
                </DrawerHeader>

                <DrawerBody>
                    <VStack spacing={5} align="stretch" py={4}>
                        <Box textAlign="center" mb={2}>
                            <Heading size="lg" fontWeight="900" mb={2}>{pipeline.name || 'Unnamed Pipeline'}</Heading>
                            <HStack justify="center" spacing={2} flexWrap="wrap">
                                <Tag colorScheme="purple" size="lg" borderRadius="full" px={4} fontWeight="bold">Asset ID: {pipeline.id}</Tag>
                                {/* --- UPDATE: lifecycle status chip --- */}
                                {pipeline.status && (
                                    <Tag
                                        size="lg" borderRadius="full" px={4} fontWeight="bold"
                                        colorScheme={EXCLUDED_FROM_SCANS.includes(pipeline.status) ? 'gray' : 'green'}
                                    >
                                        {String(pipeline.status).replace(/_/g, ' ').toUpperCase()}
                                    </Tag>
                                )}
                                {/* --- END UPDATE */}
                            </HStack>
                        </Box>

                        <Divider borderColor={borderColor} />

                        {/* ================================================== */}
                        {/* --- UPDATE: EXCAVATION CLEARANCE SECTION           */}
                        {/* depth_m and status are read directly by the 4D     */}
                        {/* subsurface scan. Surfacing them here means the     */}
                        {/* drawer that opens on a map click answers the       */}
                        {/* question a dispatcher actually has: will this main */}
                        {/* appear in a clearance check, and at what depth?    */}
                        {/* ================================================== */}
                        <Box p={4} bg={clearanceBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                            <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
                                <HStack spacing={2}>
                                    <Icon as={MdShield} color="orange.400" boxSize={5} />
                                    <Heading size="sm" fontWeight="800">Excavation Clearance</Heading>
                                </HStack>
                                {onEdit && (
                                    <Button
                                        size="xs" colorScheme="purple" variant="outline" borderRadius="full"
                                        leftIcon={<Icon as={MdEdit} />} onClick={handleEditClick}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </Flex>

                            {clearance && (
                                <Alert status={clearance.tone} borderRadius="lg" fontSize="xs" mb={3} alignItems="flex-start" py={2}>
                                    <AlertIcon as={clearance.icon} boxSize={4} />
                                    <Box>
                                        <Text fontWeight="800">{clearance.label}</Text>
                                        <Text mt={0.5}>{clearance.detail}</Text>
                                    </Box>
                                </Alert>
                            )}

                            <SimpleGrid columns={2} spacing={3}>
                                <Box>
                                    <HStack spacing={1} mb={1}>
                                        <Icon as={FaArrowDown} color={mutedColor} boxSize={2.5} />
                                        <Text fontSize="10px" fontWeight="700" color={mutedColor} textTransform="uppercase">Cover Depth</Text>
                                    </HStack>
                                    <Text fontWeight="900" fontSize="lg" color={clearance?.color}>
                                        {pipeline.depth_m !== null && pipeline.depth_m !== undefined && pipeline.depth_m !== ''
                                            ? `${pipeline.depth_m} m`
                                            : 'Unsurveyed'}
                                    </Text>
                                </Box>

                                <Box>
                                    <HStack spacing={1} mb={1}>
                                        <Icon as={FaClipboardCheck} color={mutedColor} boxSize={2.5} />
                                        <Text fontSize="10px" fontWeight="700" color={mutedColor} textTransform="uppercase">Depth Source</Text>
                                    </HStack>
                                    <Text fontWeight="700" fontSize="sm">
                                        {DEPTH_SOURCE_LABELS[pipeline.depth_source] || 'Not recorded'}
                                    </Text>
                                </Box>

                                <Box>
                                    <Text fontSize="10px" fontWeight="700" color={mutedColor} textTransform="uppercase" mb={1}>Pressure Zone</Text>
                                    <Text fontWeight="700" fontSize="sm">{pipeline.pressure_zone || 'Unassigned'}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="10px" fontWeight="700" color={mutedColor} textTransform="uppercase" mb={1}>Geometry</Text>
                                    <HStack spacing={1}>
                                        <Icon
                                            as={pipeline.geometry_srid_ok === false ? MdWarning : MdCheckCircle}
                                            color={pipeline.geometry_srid_ok === false ? badColor : okColor}
                                            boxSize={3}
                                        />
                                        <Text fontWeight="700" fontSize="sm">
                                            {Array.isArray(pipeline.path_coordinates)
                                                ? `${pipeline.path_coordinates.length} pts`
                                                : 'None'}
                                            {pipeline.geometry_srid != null && ` · SRID ${pipeline.geometry_srid}`}
                                        </Text>
                                    </HStack>
                                </Box>
                            </SimpleGrid>
                        </Box>
                        {/* --- END UPDATE */}

                        <VStack spacing={3}>
                            <InfoRow icon={MdBusiness} label="Material" value={pipeline.material} />
                            <InfoRow icon={MdStraighten} label="Diameter" value={pipeline.diameter_inches ? `${pipeline.diameter_inches} inches` : 'N/A'} />
                            {/* --- UPDATE: formatDateSafe avoids the UTC off-by-one
                                described where it is defined above. */}
                            <InfoRow icon={MdCalendarToday} label="Install Date" value={formatDateSafe(pipeline.install_date)} />
                            {/* --- END UPDATE */}
                            <InfoRow icon={MdLocationOn} label="Area Zone" value={pipeline.area_code} />
                        </VStack>
                        
                        <Divider borderColor={borderColor} pt={2} />

                        <Box>
                            <HStack mb={4} mt={2}>
                                <Icon as={MdSensors} color="blue.500" boxSize={5} />
                                <Heading size="sm" fontWeight="800">Attached Sensors ({associatedSensors.length})</Heading>
                            </HStack>
                            
                            {associatedSensors.length > 0 ? (
                                <VStack spacing={3} align="stretch">
                                    {associatedSensors.map(sensor => {
                                        const reading = readings[sensor.device_id || sensor.id];

                                        // --- UPDATE: age-aware online state --------------
                                        // isLive was `!!reading`, so a reading from three
                                        // days ago still showed ONLINE with a pulsing green
                                        // dot. Now matched to the 5-minute TTL the reading
                                        // tables use, with STALE as a third state.
                                        const readingAgeMs = reading?.timestamp
                                            ? Math.abs(Date.now() - new Date(reading.timestamp).getTime())
                                            : null;
                                        const isStale = readingAgeMs !== null && readingAgeMs > readingTtlMs;
                                        const isLive = !!reading && !isStale;

                                        // A genuine 0 PSI reading — total loss of pressure,
                                        // the single most important value on this panel —
                                        // was falling through `reading.psi || reading.level
                                        // || 0` because 0 is falsy.
                                        const displayValue = reading
                                            ? (reading.psi ?? reading.level ?? reading.value ?? null)
                                            : null;
                                        const isPressure = reading?.psi !== undefined && reading?.psi !== null;
                                        // --- END UPDATE

                                        return (
                                            <Box 
                                                key={sensor.device_id || sensor.id} 
                                                p={4} 
                                                borderWidth="1px" 
                                                borderColor={borderColor} 
                                                borderRadius="xl" 
                                                bg={cardBg} 
                                                shadow="sm" 
                                                transition="all 0.2s" 
                                                _hover={{ shadow: 'md', borderColor: 'blue.400' }}
                                            >
                                                <Flex justify="space-between" align="center" mb={2}>
                                                    <Text fontWeight="800" fontSize="sm">ID: {sensor.device_id || sensor.id}</Text>
                                                    {/* --- UPDATE: three states instead of two --- */}
                                                    <Tooltip
                                                        hasArrow
                                                        label={reading?.timestamp
                                                            ? `Last reading ${new Date(reading.timestamp).toLocaleString()}`
                                                            : 'No reading received'}
                                                    >
                                                        <Badge
                                                            colorScheme={isLive ? 'green' : isStale ? 'orange' : 'gray'}
                                                            variant="subtle" px={2} py={1} borderRadius="full"
                                                            display="flex" alignItems="center"
                                                        >
                                                            {isLive && <Box as="span" w="2" h="2" borderRadius="full" bg="green.500" mr={2} animation={`${pulseRing} 2s infinite`} />}
                                                            {isLive ? 'ONLINE' : isStale ? 'STALE' : 'OFFLINE'}
                                                        </Badge>
                                                    </Tooltip>
                                                    {/* --- END UPDATE */}
                                                </Flex>
                                                <HStack justify="space-between">
                                                    <Text fontSize="xs" color="gray.500" fontWeight="600">LATEST TELEMETRY</Text>
                                                    <Text fontWeight="900" color={isLive ? "blue.500" : "gray.500"} fontSize="lg">
                                                        {/* --- UPDATE: nullish coalescing so 0 renders --- */}
                                                        {displayValue !== null && displayValue !== undefined
                                                            ? `${displayValue}${isPressure ? ' PSI' : ''}`
                                                            : 'N/A'}
                                                        {/* --- END UPDATE */}
                                                    </Text>
                                                </HStack>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            ) : (
                                <Box p={6} textAlign="center" borderWidth="1px" borderStyle="dashed" borderColor={borderColor} borderRadius="xl">
                                    <Text color="gray.500" fontSize="sm" fontWeight="600">No sensors mapped to this pipeline.</Text>
                                </Box>
                            )}
                        </Box>
                    </VStack>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

export default PipelineInfoDrawer;